/**
 * useCatalogContext — the single source of truth for "which jurisdiction does
 * this teacher work in, and which content-language family should the catalog
 * default to". Read by the Printables sidebar, the account modal's Country /
 * Region picker and the Home AI generator, so a change made in one repaints the
 * others without a remount.
 *
 * The school-context fetch itself is NOT re-implemented here — it is imported
 * from the `useSchoolContext` shim so there stays exactly ONE module-scope
 * school-context session cache (see the note at services/useSchoolContext.js).
 *
 * Everything degrades invisibly when a worker is unreachable: the localStorage
 * mirror keeps the choice, and the jurisdiction list falls back to the
 * hardcoded US set inside groupJurisdictions().
 */

import { API_BASE } from '../config.js';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import useSchoolContext from './useSchoolContext';
import { groupJurisdictions, US_STATES } from '../components/home/StatePicker';

// Machine-local mirror of the chosen jurisdiction — same convention as
// LANGUAGE_STORAGE_KEY ('pse_language') and 'pse_state_picker_skipped'. It
// survives offline and pre-auth, where the server value is unavailable.
export const JURISDICTION_STORAGE_KEY = 'pse_jurisdiction';

// Content-language FAMILIES the catalog filter understands. Labels stay in
// their own language — never translated, same rule as LANGUAGES in i18n.
export const LANGUAGE_FAMILIES = ['en', 'es', 'pt', 'fr'];
export const LANGUAGE_FAMILY_LABELS = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
  fr: 'Français',
};
export const LANGUAGE_ALL = 'all';

// UI locale -> content-language family. Byte-equivalent to the worker's
// languageFamily() so the client never asks for a family the API cannot serve:
// en->en, es-419/es-ES->es, pt-BR/pt-PT->pt, fr->fr.
export function languageFamily(locale) {
  if (!locale) return 'en';
  const fam = String(locale).split('-')[0].toLowerCase();
  return LANGUAGE_FAMILIES.includes(fam) ? fam : 'en';
}

const PRINTABLES_API = `${API_BASE}/printables`; // Brightivity printables fork

// The account modal and the Printables sidebar are mounted at the same time, so
// a write in one has to reach the other without a remount.
const JURISDICTION_EVENT = 'pse:jurisdiction-changed';

function readStoredJurisdiction() {
  try {
    return localStorage.getItem(JURISDICTION_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

function writeStoredJurisdiction(code) {
  try {
    if (code) localStorage.setItem(JURISDICTION_STORAGE_KEY, code);
    else localStorage.removeItem(JURISDICTION_STORAGE_KEY);
  } catch { /* private mode — the server value still carries the choice */ }
}

// Module scope — one GET /api/states per app session, shared by Home, the
// account modal and Printables. Same endpoint and same setCount>0 filter as
// usePrintables' fetchJurisdictions; a bare fetch here so this hook does not
// instantiate a second usePrintables (and its poster/loading state).
let jurisdictionsPromise = null;
let jurisdictionsCache = null;

function loadJurisdictions() {
  if (!jurisdictionsPromise) {
    jurisdictionsPromise = fetch(`${PRINTABLES_API}/states`)
      .then((r) => r.json())
      .then((j) => (j?.data?.states ?? []).filter((s) => (s.setCount ?? 0) > 0))
      .then((list) => { jurisdictionsCache = list; return list; })
      .catch(() => { jurisdictionsPromise = null; return []; }); // retry on the next mount
  }
  return jurisdictionsPromise;
}

// ─── Curriculum-capable jurisdictions (contract 2026-08-24) ───────────────
// Which jurisdictions can render their OWN official curriculum as the left
// browse tree instead of the US category tree. Data-derived server-side (a
// non-US jurisdiction with at least one labelled standard set), so the list
// self-heals as ingests improve and needs no hardcoded code list here.
//
// PARAMETERLESS BY DESIGN, and that is load-bearing. It is a static reference
// list, so it can never participate in the facets feedback loop that had
// Printables oscillating forever on 2026-08-23 (facets scoped by a derived
// value → new defaults → new facets → …). Because it takes no filter input it
// is constant for the session, which keeps `effectiveJurisdiction` a fixed
// point. Do not add parameters to it.
let curriculumJurisdictionsPromise = null;
let curriculumJurisdictionsCache = null;

export function loadCurriculumJurisdictions() {
  if (!curriculumJurisdictionsPromise) {
    curriculumJurisdictionsPromise = fetch(`${PRINTABLES_API}/curriculum/jurisdictions`)
      .then((r) => r.json())
      .then((j) => (j?.data?.jurisdictions ?? []))
      .then((list) => { curriculumJurisdictionsCache = list; return list; })
      // Unreachable worker ⇒ empty list ⇒ every region keeps today's category
      // tree. Silent degradation, never an error path.
      .catch(() => { curriculumJurisdictionsPromise = null; return []; }); // retry on the next mount
  }
  return curriculumJurisdictionsPromise;
}

// The cached array is returned by identity once resolved, so consumers that
// derive a Set from it with useMemo recompute exactly once per session.
export function useCurriculumJurisdictions() {
  const [list, setList] = useState(curriculumJurisdictionsCache || []);
  useEffect(() => {
    let cancelled = false;
    loadCurriculumJurisdictions().then((l) => { if (!cancelled) setList(l); });
    return () => { cancelled = true; };
  }, []);
  return list;
}

export function useCatalogContext() {
  const { i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { stateCode, saveState, clearState, loaded } = useSchoolContext();
  const [localCode, setLocalCode] = useState(readStoredJurisdiction);
  const [jurisdictions, setJurisdictions] = useState(jurisdictionsCache || []);

  useEffect(() => {
    let cancelled = false;
    loadJurisdictions().then((list) => { if (!cancelled) setJurisdictions(list); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onChange = (e) => setLocalCode(e.detail || null);
    window.addEventListener(JURISDICTION_EVENT, onChange);
    return () => window.removeEventListener(JURISDICTION_EVENT, onChange);
  }, []);

  // Server wins on sign-in; mirroring it down keeps the choice available
  // offline and before the next school-context fetch resolves.
  useEffect(() => {
    if (!stateCode || stateCode === localCode) return;
    writeStoredJurisdiction(stateCode);
    setLocalCode(stateCode);
  }, [stateCode, localCode]);

  // The mirror is machine-local, not user-scoped: sign in as user B on a
  // machine where user A picked a country and the mirror still holds A's
  // choice. Suppressing it for authenticated users until school-context has
  // loaded means B never sees A's jurisdiction flash by.
  const jurisdiction = stateCode || ((!isAuthenticated || loaded) ? localCode : null);
  const jurisdictionSource = stateCode ? 'server' : (jurisdiction ? 'local' : null);

  const setJurisdiction = useCallback((code) => {
    const next = code || null;
    writeStoredJurisdiction(next); // synchronous + offline-safe first
    setLocalCode(next);
    window.dispatchEvent(new CustomEvent(JURISDICTION_EVENT, { detail: next }));
    // Both paths must move the SHARED school-context store, not just the mirror.
    // `jurisdiction` below resolves as `stateCode || localCode`, so a stateCode
    // left behind here shadows the pick in every other mounted consumer — which
    // is exactly what kept Printables on the old country until a reload.
    // PUT /api/user/school-state still has no clear semantics, so clearing is
    // session-local; nothing is sent for the null case.
    try {
      if (next) saveState(next);
      else clearState();
    } catch { /* fire-and-forget */ }
  }, [saveState, clearState]);

  const uiLanguageFamily = languageFamily(i18n.resolvedLanguage || i18n.language);

  const jurisdictionName = useCallback((code) => (
    jurisdictions.find((j) => j.code === code)?.name
    ?? US_STATES.find((s) => s.code === code)?.name
    ?? code
    ?? null
  ), [jurisdictions]);

  return {
    jurisdiction,
    jurisdictionSource,
    setJurisdiction,
    jurisdictions,
    jurisdictionName,
    uiLanguageFamily,
    // Alias — consumers destructure it under both names.
    languageFamily: uiLanguageFamily,
    loaded,
  };
}

export default useCatalogContext;

// Re-exported so a consumer needs one import to render a grouped picker.
export { groupJurisdictions, US_STATES };
